/**
 * EDC Glossary hub + term pages (templates/page.glossary.liquid, templates/metaobject/edc_*.liquid).
 * - Hero instant search: suggestions combobox (arrow keys, Enter, Escape); mirrors into the A–Z list search; "/" focuses it.
 * - No match: offers "Submit it" with the query prefilled in the contribution form.
 * - Category rows: press the matching chip and scroll to the list.
 * - Term-of-the-day book: prev / next / random with a page-turn (fade under reduced motion).
 * - Contribution dialog: new term or suggest an edit; duplicate check against existing terms; reopens after a post.
 */
const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const readJSON = (sel) => { try { return JSON.parse($(sel)?.textContent || '[]'); } catch { return []; } };

const terms = readJSON('[data-gh-terms]');

/* ---------- Contribution dialog ---------- */
const dialog = $('[data-gh-dialog]');

function setMode(mode, term = '') {
  if (!dialog) return;
  const edit = mode === 'edit';
  dialog.dataset.mode = mode;
  $('[data-gh-title]', dialog).textContent = edit ? dialog.dataset.titleEdit : dialog.dataset.titleNew;
  for (const intro of $$('[data-gh-intro]', dialog)) intro.textContent = edit ? dialog.dataset.introEdit.replace('__T__', term) : dialog.dataset.introNew;
  for (const wrap of $$('[data-gh-new]', dialog)) {
    wrap.hidden = edit;
    for (const field of $$('input, select, textarea', wrap)) field.disabled = edit;
  }
  const editTerm = $('[data-gh-edit-term]', dialog);
  editTerm.disabled = !edit;
  editTerm.value = edit ? term : '';
  $('[data-gh-kind]', dialog).value = edit ? `Suggested edit: ${term}` : 'New term';
  $('[data-gh-new-label]', dialog).hidden = edit;
  $('[data-gh-edit-label]', dialog).hidden = !edit;
  const body = $('#gh-body', dialog);
  body.placeholder = edit ? body.dataset.phEdit : body.dataset.phNew;
  $('[data-gh-dup]', dialog).hidden = true;
}

function openDialog(mode, term = '') {
  if (!dialog) return;
  setMode(mode, term);
  if (mode === 'new' && term) {
    const input = $('[data-gh-term-input]', dialog);
    input.value = term;
    checkDuplicate();
  }
  if (!dialog.open) dialog.showModal();
  const first = mode === 'edit' ? $('#gh-body', dialog) : $('[data-gh-term-input]', dialog);
  // Phones: no auto focus, so the keyboard does not cover the form before the visitor has read it (it opens on tap).
  if (window.matchMedia('(min-width: 48rem)').matches) requestAnimationFrame(() => first?.focus());
}

function checkDuplicate() {
  const input = $('[data-gh-term-input]', dialog);
  const box = $('[data-gh-dup]', dialog);
  if (!input || !box) return;
  const q = input.value.trim().toLowerCase();
  const hit = q && terms.find((t) => t.t.toLowerCase() === q || t.f.toLowerCase() === q);
  box.hidden = !hit;
  if (hit) {
    $('[data-gh-dup-text]', box).textContent = box.dataset.label.replace('__T__', hit.t);
    box.dataset.term = hit.t;
  }
}

if (dialog) {
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-gh-open]');
    if (!opener) return;
    event.preventDefault();
    let term = opener.dataset.ghTerm || '';
    if (opener.hasAttribute('data-gh-from-search')) term = ($('[data-gi-search]')?.value || '').trim();
    if (opener.dataset.ghOpen === 'edit' && !term) term = dialog.dataset.defaultTerm || '';
    openDialog(opener.dataset.ghOpen, term);
  });
  $('[data-gh-close]', dialog)?.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
  $('[data-gh-term-input]', dialog)?.addEventListener('input', checkDuplicate);
  $('[data-gh-dup-edit]', dialog)?.addEventListener('click', () => {
    const term = $('[data-gh-dup]', dialog).dataset.term;
    setMode('edit', term);
    $('#gh-body', dialog)?.focus();
  });

  // After a post the page reloads: reopen with the thank-you (or errors) in place.
  const posted = $('[data-gh-posted]', dialog);
  if (posted) {
    const success = posted.getAttribute('role') === 'status';
    if (success) dialog.dataset.done = '';
    setMode('new');
    dialog.showModal();
  }
}

/* ---------- Hero instant search ---------- */
const wrap = $('[data-gh-search-wrap]');
if (wrap) {
  const input = $('[data-gh-search]', wrap);
  const list = $('[data-gh-suggest]', wrap);
  const listSearch = $('[data-gi-search]');
  let active = -1;
  let options = [];

  const close = () => { list.hidden = true; input.setAttribute('aria-expanded', 'false'); input.removeAttribute('aria-activedescendant'); active = -1; };
  const setActive = (i) => {
    options.forEach((o, n) => o.setAttribute('aria-selected', String(n === i)));
    active = i;
    if (options[i]) input.setAttribute('aria-activedescendant', options[i].id);
  };
  const score = (t, words) => {
    const name = t.t.toLowerCase();
    const hay = `${name} ${t.f.toLowerCase()} ${t.c.toLowerCase()}`;
    if (!words.every((w) => hay.includes(w))) return -1;
    if (name === words.join(' ')) return 3;
    if (name.startsWith(words[0])) return 2;
    return 1;
  };
  const mark = (text, words) => {
    let out = esc(text);
    for (const w of words) out = out.replace(new RegExp(`(${w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig'), '<mark>$1</mark>');
    return out;
  };

  const render = () => {
    const q = input.value.trim();
    if (listSearch) { listSearch.value = q; listSearch.dispatchEvent(new Event('input', { bubbles: true })); }
    if (!q) { close(); return; }
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    const hits = terms.map((t) => [score(t, words), t]).filter(([s]) => s > 0).sort((a, b) => b[0] - a[0] || a[1].t.localeCompare(b[1].t));
    let html = '';
    hits.slice(0, 6).forEach(([, t], i) => {
      html += `<li role="option" id="gh-opt-${i}" class="gh-suggest__opt" data-url="${esc(t.u)}" aria-selected="false"><span class="gh-suggest__term">${mark(t.t, words)}</span>${t.f !== t.t ? `<span class="gh-suggest__full">${mark(t.f, words)}</span>` : ''}<span class="gh-suggest__cat">${esc(t.c)}</span></li>`;
    });
    if (hits.length > 6) {
      html += `<li role="option" id="gh-opt-all" class="gh-suggest__opt gh-suggest__all" data-all aria-selected="false">${esc(wrap.dataset.labelAll.replace('__COUNT__', hits.length))} →</li>`;
    }
    if (!hits.length) {
      html += `<li role="option" id="gh-opt-new" class="gh-suggest__opt gh-suggest__none" data-new aria-selected="false"><span>${esc(wrap.dataset.labelNone.replace('__Q__', q))}</span><span class="gh-suggest__cta">${esc(wrap.dataset.labelSubmit)} →</span></li>`;
    }
    list.innerHTML = html;
    options = $$('[role="option"]', list);
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    setActive(hits.length ? 0 : -1);
  };

  const choose = (opt) => {
    if (!opt) return;
    if (opt.dataset.url) { location.href = opt.dataset.url; return; }
    if (opt.hasAttribute('data-new')) { const q = input.value.trim(); close(); openDialog('new', q); return; }
    close();
    $('#browse')?.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
  };

  input.addEventListener('input', render);
  input.addEventListener('focus', () => { if (input.value.trim()) render(); });
  input.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown' && options.length) { event.preventDefault(); setActive((active + 1) % options.length); }
    else if (event.key === 'ArrowUp' && options.length) { event.preventDefault(); setActive((active - 1 + options.length) % options.length); }
    else if (event.key === 'Enter') { event.preventDefault(); choose(options[active] || $('[data-all]', list) || $('[data-new]', list)); }
    else if (event.key === 'Escape') { if (!list.hidden) { close(); } else { input.value = ''; render(); } }
  });
  list.addEventListener('mousedown', (event) => event.preventDefault());
  list.addEventListener('click', (event) => choose(event.target.closest('[role="option"]')));
  list.addEventListener('mousemove', (event) => { const o = event.target.closest('[role="option"]'); if (o) setActive(options.indexOf(o)); });
  input.addEventListener('blur', () => setTimeout(close, 120));
  document.addEventListener('keydown', (event) => {
    if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
    const tag = document.activeElement?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || document.activeElement?.isContentEditable || dialog?.open) return;
    event.preventDefault();
    input.focus();
  });
}

/* ---------- Category rows -> chips ---------- */
for (const row of $$('[data-gh-row]')) {
  row.addEventListener('click', () => {
    $(`[data-gi-filter="${CSS.escape(row.dataset.ghRow)}"]`)?.click();
    $('#glossary-index-list')?.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
  });
}


/* ---------- A–Z list: fixed-height scroll area (merchant 2026-10-30) so the community sections stay reachable ---------- */
const scroller = $('[data-gh-scroll]');
if (scroller) {
  const scrollToEl = (el) => {
    const top = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
    scroller.scrollTo({ top: Math.max(0, top - 4), behavior: reduceMotion() ? 'auto' : 'smooth' });
  };
  // Letter links scroll the list itself, not the page.
  for (const link of $$('[data-gi-letter]')) {
    link.addEventListener('click', (event) => {
      const target = document.getElementById(`letter-${link.dataset.giLetter}`);
      if (!target || target.hidden) return;
      event.preventDefault();
      scrollToEl(target);
    });
  }
  // New search or category: start the list from the top.
  const reset = () => { scroller.scrollTop = 0; };
  $('[data-gi-search]')?.addEventListener('input', reset);
  for (const chip of $$('[data-gi-filter]')) chip.addEventListener('click', reset);
  // Deep link #term-x: scroll the list to it.
  const hashTerm = decodeURIComponent(location.hash.slice(1));
  if (hashTerm.startsWith('term-')) {
    const row = document.getElementById(hashTerm);
    if (row && scroller.contains(row)) requestAnimationFrame(() => { scroller.scrollIntoView({ block: 'start' }); scrollToEl(row); });
  }
}

/* ---------- Term-of-the-day book ---------- */
const book = $('[data-gh-book]');
const pages = readJSON('[data-gh-book-data]');
if (book && pages.length > 1) {
  const page = $('[data-gh-page]', book);
  let index = Number(book.dataset.index) || 0;
  let busy = false;
  const fill = (p) => {
    $('[data-f="cat"]', page).textContent = p.c;
    $('[data-f="term"]', page).textContent = p.t;
    const full = $('[data-f="full"]', page);
    full.textContent = p.f;
    full.hidden = p.f === p.t;
    $('[data-f="desc"]', page).textContent = p.d;
    $('[data-f="by"]', page).textContent = p.b;
    $('[data-f="url"]', page).href = p.u;
  };
  // Apple-style content transition (product pages / Apple Music editorial cards): the paper stays put, its lines blur out and
  // rise back in one after another with a soft spring ease; the paper itself just "breathes". No 3D flip.
  const lines = () => $$('[data-f]', page).filter((el) => !el.hidden);
  const turn = async (next, dir) => {
    if (busy || next === index) return;
    busy = true;
    const shift = dir > 0 ? 1 : -1;
    if (reduceMotion()) {
      await page.animate([{ opacity: 1 }, { opacity: 0.2 }], { duration: 150, fill: 'forwards' }).finished;
      fill(pages[next]);
      await page.animate([{ opacity: 0.2 }, { opacity: 1 }], { duration: 220, fill: 'forwards' }).finished;
    } else {
      page.animate([{ transform: 'scale(1)' }, { transform: 'scale(0.985)' }, { transform: 'scale(1)' }], { duration: 900, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' });
      const out = lines().map((el, i) => el.animate(
        [{ opacity: 1, transform: 'none', filter: 'blur(0)' }, { opacity: 0, transform: `translateY(${-10 * shift}px)`, filter: 'blur(6px)' }],
        { duration: 260, delay: i * 30, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' }
      ).finished);
      await Promise.all(out);
      fill(pages[next]);
      const incoming = lines().map((el, i) => el.animate(
        [{ opacity: 0, transform: `translateY(${18 * shift}px)`, filter: 'blur(8px)' }, { opacity: 1, transform: 'none', filter: 'blur(0)' }],
        { duration: 720, delay: 60 + i * 70, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'both' }
      ));
      await Promise.all(incoming.map((a) => a.finished));
      for (const a of incoming) a.cancel();
    }
    for (const a of page.getAnimations({ subtree: true })) a.cancel();
    index = next;
    busy = false;
  };
  $('[data-gh-next]', book)?.addEventListener('click', () => turn((index + 1) % pages.length, 1));
  $('[data-gh-prev]', book)?.addEventListener('click', () => turn((index - 1 + pages.length) % pages.length, -1));
  $('[data-gh-random]', book)?.addEventListener('click', () => {
    let next = index;
    while (next === index) next = Math.floor(Math.random() * pages.length);
    turn(next, 1);
  });
} else if (book) {
  for (const b of $$('[data-gh-prev], [data-gh-next], [data-gh-random]', book)) b.hidden = true;
}

/* ---------- Docked search: always reachable while scrolling (MDN / Apple Support pattern) ---------- */
{
  const slot = $('[data-gh-slot]');
  const dock = $('[data-gh-dock]');
  const dockSlot = $('[data-gh-dock-slot]');
  const searchEl = slot && $('[data-gh-search-wrap]', slot);
  if (slot && dock && dockSlot && searchEl) {
    const header = document.querySelector('.sticky.top-0.z-40') || document.querySelector('header');
    const headerBottom = () => (header ? Math.max(0, header.getBoundingClientRect().bottom) : 0);
    let docked = false;
    const move = (toDock) => {
      if (toDock === docked) return;
      const input = $('[data-gh-search]', searchEl);
      const hadFocus = document.activeElement === input;
      slot.style.minHeight = `${slot.offsetHeight}px`;
      (toDock ? dockSlot : slot).append(searchEl);
      dock.classList.toggle('is-on', toDock);
      docked = toDock;
      if (hadFocus) input.focus({ preventScroll: true });
    };
    const update = () => {
      const top = headerBottom();
      dock.style.top = `${top}px`;
      move(slot.getBoundingClientRect().bottom < top + 8);
    };
    window.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update);
    update();
  }
}

/* ---------- Deep link ?topic=<handle> from term pages: open the hub filtered to that topic ---------- */
{
  const topic = new URLSearchParams(location.search).get('topic');
  const chip = topic && $(`[data-gi-filter="${CSS.escape(topic)}"]`);
  if (chip) requestAnimationFrame(() => { chip.click(); document.getElementById('browse')?.scrollIntoView({ block: 'start' }); });
}

/* ---------- Category nav "Submit a term": smooth scroll to the contribute section ---------- */
for (const link of document.querySelectorAll('[data-gf-join]')) {
  link.addEventListener('click', (event) => {
    const target = document.getElementById('contribute');
    if (!target) return;
    event.preventDefault();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(null, '', '#contribute');
  });
}
